import type { FieldValues } from 'react-hook-form';
import type {
  AccountInternalMap,
  BankDispensary,
  CreateDocumentsRequest,
  CustomRequirementResult,
  Dispensary,
  DocumentUpload,
  GcdAccountOwnershipRole,
  GcdUserRole,
  InitialUserInfo,
  InviteNewUserRequest,
  MinifiedAccountUser,
  MinifiedCrbTemplateInfo,
  MinifiedDispensary,
  MUO,
  MUOBankResponse,
  OnboardingDocumentRequirementResult,
  PayqwickUserRole,
  TemplateResultResponse,
  User,
  UserIdentification,
  USStates
} from '@gcv/shared';
import {
  ArchivedReason,
  DueDiligenceStatus,
  IANATimezones,
  OrganizationRoleResolver,
  OrganizationType
} from '@gcv/shared';
import { action, runInAction } from 'mobx';

import { BanksApi, DispensariesApi, DocumentsApi, QuestionnairesApi, TemplateApi, UsersApi } from 'api';
import { environment } from 'environments/environment';
import { getAppViewStateStore } from 'stores/AppViewStateStore';
import { getComplianceStore } from 'stores/ComplianceStore';
import { getFiBankStore } from 'stores/FiBankStore';
import { getFiDispensaryStore } from 'stores/FiDispensaryStore';
import { getFiRelationshipManagerStore } from 'stores/FiRelationshipManagerStore';
import { getFiTemplateStore } from 'stores/FiTemplateStore';
import { getSnackbarStore, SnackbarSeverity } from 'stores/SnackBarStore';
import { getUserStore } from 'stores/UserStore';
import type { FilterListChild } from 'ui';
import { getCustomFieldsLastModifiedDateText } from 'util/additional-info.util';
import { sortArrayByField } from 'util/array.util';
import { DateTimeHelpers } from 'util/dateTime.util';
import { lookupAccountNumberError } from 'util/error-lookup.util';
import { getMimeType, openFileInNewWindow } from 'util/files.util';
import { getModifiedDateText } from 'util/format.util';
import { getObjectMap } from 'util/objectUtils';
import { getLicenseType } from 'util/org.util';
import { uploadUserDocumentToS3 } from 'util/s3.util';
import type { IdentificationDocument, InvitedDispensary, Model } from './model';
import { model } from './model';

export interface TemplateOption {
  value: string; // The Option from 'ui' allows number or boolean, and we can't.
  label: string;
  published: boolean;
  plant_touching: boolean;
}

class Actions {
  constructor(private model: Model) {
    this.resetMuos();
  }

  dispensariesApi = new DispensariesApi();
  userApi = new UsersApi();
  documentsApi = new DocumentsApi();
  banksApi = new BanksApi();
  templateApi = new TemplateApi();
  questionnairesApi = new QuestionnairesApi();

  resolver = new OrganizationRoleResolver();
  appViewStore = getAppViewStateStore();
  userStore = getUserStore();
  snackbarStore = getSnackbarStore();
  dispensaryStore = getFiDispensaryStore();
  complianceStore = getComplianceStore();
  bankStore = getFiBankStore();
  bankUserStore = getFiRelationshipManagerStore();
  searchTerms = [];

  resetMuos = () => {
    this.model.filteredBankMuos = this.dispensaryStore.bankMuos;
  };

  loadPermissions = action(() => {
    if (this.model.hasLoadedPermissions) {
      return;
    }

    this.model.isBankExaminer = this.resolver.userHasRole(
      this.bankStore.bank.groups,
      this.userStore.user,
      'bank_examiner'
    );

    this.model.canUserApproveAccounts =
      !this.model.isBankExaminer &&
      this.resolver.userCanDoAction(this.bankStore.bank.groups, this.userStore.user, 'account_info_update');

    this.model.canUserApproveDocuments = this.resolver.userCanDoAction(
      this.bankStore.bank.groups,
      this.userStore.user,
      'document_approve'
    );

    this.model.canUserApproveRequirements =
      !this.model.isBankExaminer &&
      this.resolver.userCanDoAction(this.bankStore.bank.groups, this.userStore.user, 'requirement_approve');

    this.model.canUserUploadCommentAndRequest = this.resolver.userCanDoAction(
      this.bankStore.bank.groups,
      this.userStore.user,
      'upload_requirement_files'
    );

    this.model.userCanEditOrganizationalDetails = this.resolver.userCanDoAction(
      this.bankStore.bank.groups,
      this.userStore.user,
      'edit_crb_info'
    );

    this.model.canUserInviteUsers =
      !this.model.isBankExaminer &&
      this.resolver.userCanDoAction(this.bankStore.bank.groups, this.userStore.user, 'user_add');

    runInAction(() => {
      this.model.hasLoadedPermissions = true;
    });
  });

  setAccountsSearchTerm = action((value: string) => {
    this.model.accountsSearchTerm = value;
  });

  sortDispensaries = action(async (minDispensaries: MinifiedDispensary[]) => {
    this.model.isSorting = true;
    const activeMinDispensaries: MinifiedDispensary[] = [];
    const waitingDispensaries: MinifiedDispensary[] = [];
    const reviewDispensaries: MinifiedDispensary[] = [];
    const invitedDispensaries: InvitedDispensary[] = [];

    minDispensaries.forEach((dispensary: MinifiedDispensary) => {
      if (this.isActive(dispensary)) {
        activeMinDispensaries.push(dispensary);
      } else if (this.isWaiting(dispensary)) {
        waitingDispensaries.push(dispensary);
      } else if (this.isReviewInProgress(dispensary)) {
        reviewDispensaries.push(dispensary);
      } else if (this.isInvited(dispensary)) {
        // need to add the invited prop to make this an invited dispensary
        // invited dispensaries are in the invitation table not full dispensaries
        const iDispensary = { ...dispensary, invited: false };
        invitedDispensaries.push(iDispensary as InvitedDispensary);
      }
    });
    const additionalInvitedDisps = await this.mapInvitedDispensaries();

    runInAction(() => {
      this.model.activeDispensaries = activeMinDispensaries;
      this.model.waitingDispensaries = waitingDispensaries;
      this.model.reviewDispensaries = reviewDispensaries;
      this.model.invitedDispensaries = [...invitedDispensaries, ...additionalInvitedDisps];
      this.model.isSorting = false;
    });
  });

  mapInvitedDispensaries = action(async () => {
    const invitationDispensaries = this.dispensaryStore.invitedDispensaries;
    const disps: InvitedDispensary[] = invitationDispensaries.map((dispensary) => {
      return {
        id: dispensary.id,
        name: dispensary.data.name,
        city: '--',
        state: dispensary.data.state as USStates,
        assigned_onboarding_template: {
          plant_touching: true,
          template_id: dispensary.data.template_id,
          template_version: '0',
          latest_version: '0',
          template_result_id: '0',
          assigned_date: '',
          status: DueDiligenceStatus.BANK_PENDING,
          last_updated: ''
        },
        last_login_date: '',
        status: DueDiligenceStatus.BANK_PENDING,
        archived_meta_data: {
          archived_reason: ArchivedReason.OTHER,
          archived_date: ''
        },
        orgType: OrganizationType.BUSINESS,
        industryType: dispensary.data.industryType,
        iana_timezone: IANATimezones.America_LosAngeles,
        streetAddress: '',
        postalCode: '',
        methodOfTransportation: { bank: [], vendor: [], user: [] },
        invited: dispensary.status === 'pending' ? true : false,
        date_created: '',
        invited_by_email: dispensary.email,
        invited_by_org_id: dispensary.invited_by_org_id,
        invited_id: dispensary.id
      };
    });
    return disps;
  });

  filterDispensaries = action(async (filterTerms: FilterListChild[]) => {
    if (!filterTerms.length) {
      this.sortDispensaries(this.dispensaryStore.dispensaries);

      this.model.filteredBankMuos = this.dispensaryStore.bankMuos;
      return;
    }

    const newFilterTerms: { [T: string]: string[] } = {};
    filterTerms.forEach((term) => {
      term.parentValue = term.parentValue ? term.parentValue : 'unknown';
      newFilterTerms[term.parentValue] = newFilterTerms[term.parentValue]
        ? [...newFilterTerms[term.parentValue], term.value]
        : [term.value];
    });

    const dedupedFilterTerms: { [T: string]: Set<string> } = {};
    Object.keys(newFilterTerms).forEach((filterTerm) => {
      dedupedFilterTerms[filterTerm] = new Set(newFilterTerms[filterTerm]);
    });

    const filterFn = (dispensary: MinifiedDispensary | MUO) => {
      let stateFiltered = false;
      let licenseFiltered = false;
      let muoFiltered = false;
      let typeFiltered = false;
      let relationshipManagerFiltered = false;
      let dueDiligenceFiltered = false;
      let industryTypeFiltered = false;

      if (dedupedFilterTerms['due_diligence']) {
        if (dispensary.orgType === 'dispensary') {
          const templateId = dispensary?.assigned_onboarding_template?.template_id;
          const templateName = getFiTemplateStore().templates.onboarding?.[templateId]?.name;

          if (templateName) {
            dueDiligenceFiltered = dedupedFilterTerms['due_diligence'].has(templateName);
          } else {
            dueDiligenceFiltered = dedupedFilterTerms['due_diligence'].has('--');
          }
        } else if (dispensary.orgType === 'muo') {
          dueDiligenceFiltered = dedupedFilterTerms['due_diligence'].has('--');
        }
      } else {
        dueDiligenceFiltered = true;
      }

      if (dedupedFilterTerms['relationship_manager']) {
        const relationshipManager = this.bankUserStore.relationshipManagers[dispensary.id];

        if (relationshipManager) {
          relationshipManagerFiltered = dedupedFilterTerms['relationship_manager'].has(relationshipManager);
        } else if (dedupedFilterTerms['relationship_manager'].has('--')) {
          relationshipManagerFiltered = true;
        }
      } else {
        relationshipManagerFiltered = true;
      }

      if (dedupedFilterTerms['industry_type']) {
        industryTypeFiltered = dedupedFilterTerms['industry_type'].has(
          (dispensary as MinifiedDispensary).industryType
        );
      } else {
        industryTypeFiltered = true;
      }

      if (dedupedFilterTerms['parent_account']) {
        if (dispensary.orgType === 'dispensary' && dispensary.muo_id) {
          muoFiltered = dedupedFilterTerms['parent_account'].has(dispensary.muo_id);
        }
      } else {
        muoFiltered = true;
      }

      if (dedupedFilterTerms['type_of_account']) {
        const isMuo = dedupedFilterTerms['type_of_account'].has('muo');
        const isDispensary = dedupedFilterTerms['type_of_account'].has('dispensary_location');

        if (isMuo && dispensary.orgType === 'muo') {
          typeFiltered = dispensary.orgType === 'muo';
        }
        if (isDispensary && dispensary.orgType === 'dispensary' && dispensary.muo_id) {
          typeFiltered = dispensary.orgType === 'dispensary' && dispensary.muo_id != undefined;
        }
      } else {
        typeFiltered = true;
      }

      if (dedupedFilterTerms['state'] && dispensary.state) {
        stateFiltered = dedupedFilterTerms['state'].has(dispensary.state);
      } else {
        stateFiltered = true;
      }

      if (dispensary.orgType === 'dispensary' && dedupedFilterTerms['license_type']) {
        licenseFiltered = dedupedFilterTerms['license_type'].has(getLicenseType(dispensary));
      } else if (dispensary.orgType === 'muo' && dedupedFilterTerms['license_type']) {
        if (dedupedFilterTerms['license_type']?.has('--')) licenseFiltered = true;
        else licenseFiltered = false;
      } else {
        licenseFiltered = true;
      }

      return (
        stateFiltered &&
        licenseFiltered &&
        muoFiltered &&
        typeFiltered &&
        relationshipManagerFiltered &&
        dueDiligenceFiltered &&
        industryTypeFiltered
      );
      // this should be an overlap between the searched terms
    };

    const newDispensaryList = this.dispensaryStore.dispensaries.filter(filterFn);

    runInAction(() => {
      this.model.filteredBankMuos = this.dispensaryStore.bankMuos.filter(filterFn);
    });
    this.sortDispensaries(newDispensaryList);
  });

  isActive = (dispensary: MinifiedDispensary | MUOBankResponse) => {
    return dispensary.assigned_onboarding_template.status === DueDiligenceStatus.BANK_APPROVED;
  };

  isArchived = (dispensary: MinifiedDispensary | MUOBankResponse) => {
    return dispensary.assigned_onboarding_template.status === DueDiligenceStatus.BANK_DISCONNECTED;
  };

  isReviewInProgress = (dispensary: MinifiedDispensary | MUOBankResponse) => {
    return dispensary.assigned_onboarding_template.status === DueDiligenceStatus.BANK_REVIEW_IN_PROGRESS;
  };

  isWaiting = (dispensary: MinifiedDispensary | MUOBankResponse) => {
    return dispensary.assigned_onboarding_template.status === DueDiligenceStatus.BANK_AWAITING_REVIEW;
  };

  isInvited = (dispensary: MinifiedDispensary | MUOBankResponse) => {
    const status = dispensary.assigned_onboarding_template.status;

    return (
      status === DueDiligenceStatus.BANK_IN_PROGRESS ||
      status === DueDiligenceStatus.BANK_PENDING ||
      status === DueDiligenceStatus.GCV_PENDING ||
      status === DueDiligenceStatus.GCV_IN_PROGRESS ||
      status === DueDiligenceStatus.GCV_AWAITING_REVIEW ||
      status === DueDiligenceStatus.GCV_APPROVED
    );
  };

  isCurrentOrgArchived = () => {
    const minifiedDisp =
      this.dispensaryStore.bankMuosArchived.find((d) => d.id === this.model.currentOrg.id) ??
      this.dispensaryStore.dispensariesArchived.find((d) => d.id === this.model.currentOrg.id);
    if (minifiedDisp) {
      return this.isArchived(minifiedDisp);
    }
    return false;
  };

  setCurrentDispensaryById = action(async (dispensaryId: string) => {
    //get and set org here
    this.model.loadingDispensary = true;
    const dispensary = await this.banksApi.getBankDispensary(this.bankStore.bank.id, dispensaryId);
    const minifiedDisp = this.dispensaryStore.findDispensary(dispensaryId);

    runInAction(() => {
      this.model.currentOrg = dispensary;
      this.model.currentOrgMetadata = minifiedDisp?.internal_metadata;
    });

    // Prefer the freshly-fetched dispensary's assigned_onboarding_template over the
    // MobX store's copy: after the seedling wizard runs, the store still holds the
    // pre-update template_result_id, which would mismatch the new template_id.
    const freshTemplateInfo =
      'assigned_onboarding_template' in dispensary ? dispensary.assigned_onboarding_template : undefined;

    await Promise.all([
      this.setCurrentDispensaryTemplateInfo(dispensaryId, freshTemplateInfo),
      this.setDispensaryStaff(dispensaryId),
      this.getDocuments()
    ]);

    runInAction(() => {
      this.model.loadingDispensary = false;
    });
  });

  getQuestionnaires = action(async (dispId: string) => {
    try {
      const questionnaireDistForActiveQuestionnaires =
        await this.questionnairesApi.getQuestionnaireOverviewByCrb(this.bankStore.bank.id, dispId);

      runInAction(() => {
        this.model.questionnairesForAccountReview = questionnaireDistForActiveQuestionnaires;
      });
    } catch (error) {
      this.snackbarStore.showErrorSnackbarMessage('Could not load questionnaires');
    }
  });

  getTemplateRequirement = action(async (templateId: string, type: string) => {
    try {
      const template = await this.templateApi.getBankTemplate(this.bankStore.bank.id, templateId, 'latest');
      runInAction(() => {
        if (type === 'child') {
          this.model.onboardingChildTemplate = template;
        } else if (type === 'new') {
          this.model.newDueDiligenceTemplate = template;
        }
      });
    } catch (e) {
      this.snackbarStore.showErrorSnackbarMessage(
        'There was an issue loading page data. Contact support for additional help.'
      );
    }
  });

  setCurrentDispensaryTemplateInfo = action(
    async (dispensaryId: string, freshTemplateInfo?: MinifiedCrbTemplateInfo) => {
      try {
        let templateInfo: MinifiedCrbTemplateInfo;
        if (freshTemplateInfo) {
          templateInfo = freshTemplateInfo;
        } else {
          const dispensary = this.dispensaryStore.findDispensary(dispensaryId);
          if (!dispensary) {
            throw new Error('Dispensary not found');
          }
          templateInfo = dispensary.assigned_onboarding_template;
        }

        const templateResult = await this.templateApi.getCrbTemplateResult(
          dispensaryId,
          templateInfo.template_id,
          templateInfo.template_result_id
        );
        // When freshTemplateInfo is provided we trust its template_version (just-fetched
        // BankDispensary); otherwise fall back to the templateResult's version.
        const templateVersion = freshTemplateInfo
          ? freshTemplateInfo.template_version
          : templateResult.template_version ?? 'latest';
        const template = await this.templateApi.getBankTemplate(
          this.bankStore.bank.id,
          templateInfo.template_id,
          templateVersion
        );

        const resultsMap = getObjectMap(templateResult.requirements_results, 'requirement_id');
        runInAction(() => {
          this.model.templateInfo = templateInfo;
          this.model.onboardingTemplate = template;
          this.model.onboardingTemplateResult = templateResult;
          this.model.onboardingDocumentResultMap = resultsMap;
          this.model.initialCustomRequirementResults = templateResult.custom_requirement_result;
          this.model.additionalInfoLastUpdated = getCustomFieldsLastModifiedDateText(templateResult);
        });
      } catch (e) {
        this.snackbarStore.showErrorSnackbarMessage(
          'There was an issue loading page data. Contact support for additional help.'
        );
      }
    }
  );

  setOnboardingTemplateResult = action((onboardingTemplateResult: TemplateResultResponse) => {
    const resultsMap = getObjectMap(onboardingTemplateResult.requirements_results, 'requirement_id');
    this.model.onboardingDocumentResultMap = resultsMap;
    this.model.onboardingTemplateResult = onboardingTemplateResult;
    this.model.initialCustomRequirementResults = onboardingTemplateResult.custom_requirement_result;
  });

  updateOnboardingDocumentRequirementResult = action(
    (requirementResult: OnboardingDocumentRequirementResult) => {
      const existingResult = this.model.onboardingTemplateResult.requirements_results.find(
        (r) => r.requirement_id === requirementResult.requirement_id
      );
      if (existingResult) {
        const index = this.model.onboardingTemplateResult.requirements_results.indexOf(existingResult);
        this.model.onboardingTemplateResult.requirements_results.splice(index, 1, requirementResult);
      } else {
        this.model.onboardingTemplateResult.requirements_results.push(requirementResult);
      }
      this.model.onboardingDocumentResultMap[requirementResult.requirement_id] = requirementResult;
    }
  );

  updateDispensaryTemplateResult = action((templateResult: TemplateResultResponse) => {
    const resultsMap = getObjectMap(templateResult.requirements_results, 'requirement_id');
    this.model.onboardingTemplateResult = templateResult;
    this.model.onboardingDocumentResultMap = resultsMap;

    // update the status on minified dispensaries to reflect change on the accounts tables
    const minifiedDispensary = this.dispensaryStore.dispensaries.find(
      (d) => d.id === this.model.currentOrg.id
    );
    if (minifiedDispensary) {
      minifiedDispensary.assigned_onboarding_template.status = templateResult.status;
      minifiedDispensary.assigned_onboarding_template.reviewed_date = templateResult.reviewed_date;
      this.dispensaryStore.updateMinifiedDispensary(minifiedDispensary);
      this.sortDispensaries(this.dispensaryStore.dispensaries);
    }
  });

  // TODO don't rely on this after we start using new API calls. We should be able to
  // update the model using the responses from the API calls
  getUpdatedDispensaryTemplateResults = action(async (dispensaryId: string) => {
    try {
      const templateResult = await this.templateApi.getCrbTemplateResult(
        dispensaryId,
        this.model.onboardingTemplate.template_id,
        this.model.onboardingTemplateResult.id
      );
      const resultsMap = getObjectMap(templateResult.requirements_results, 'requirement_id');
      runInAction(() => {
        this.model.onboardingTemplateResult = templateResult;
        this.model.onboardingDocumentResultMap = resultsMap;
      });
    } catch (e) {
      this.snackbarStore.showErrorSnackbarMessage(
        'There was an issue loading page data. Contact support for additional help.'
      );
    }
  });

  setTemplateResults = action(
    (
      templateResult: TemplateResultResponse,
      resultsMap: {
        [requirement_id: string]: OnboardingDocumentRequirementResult;
      }
    ) => {
      this.model.onboardingTemplateResult = templateResult;
      this.model.onboardingDocumentResultMap = resultsMap;
    }
  );

  saveAdditionalInfoSection = action(async (sectionId: string, customFieldValues: Record<string, any>) => {
    try {
      runInAction(() => {
        this.model.isSavingAdditionalInfo = true;
      });

      if (!this.model.onboardingTemplate.custom_requirements[sectionId].archived) {
        const updatedSection = await this.templateApi.updateBankCrbTemplateRequirement(
          this.model.onboardingTemplateResult.crb_id,
          this.model.onboardingTemplateResult.bank_id,
          this.model.onboardingTemplateResult.template_id,
          sectionId,
          customFieldValues
        );
        this.updateCustomRequirementResults(updatedSection);
      }
    } catch (e) {
      this.snackbarStore.showErrorSnackbarMessage(
        'An error has occurred saving additional info. Please contact support@greencheckverified.com for additional help.'
      );
    } finally {
      runInAction(() => {
        this.model.isSavingAdditionalInfo = false;
      });
    }
  });

  updateCustomRequirementResults = action((requirementResult: CustomRequirementResult) => {
    const existingRequirement = this.model.onboardingTemplateResult.custom_requirement_result.find(
      (r) => r.requirement_id === requirementResult.requirement_id
    );
    if (existingRequirement) {
      const index =
        this.model.onboardingTemplateResult.custom_requirement_result.indexOf(existingRequirement);
      this.model.onboardingTemplateResult.custom_requirement_result.splice(index, 1, requirementResult);
    } else {
      this.model.onboardingTemplateResult.custom_requirement_result.push(requirementResult);
    }
    this.model.additionalInfoLastUpdated = getModifiedDateText(
      requirementResult.custom_fields.last_updated_date
    );
    this.model.onboardingTemplateResult.custom_requirement_modified_by =
      requirementResult.custom_fields.last_updated_by;
  });

  completeAddtionalInfo = action(async () => {
    try {
      runInAction(() => {
        this.model.isCompletingAdditionalInfo = true;
      });
      await this.templateApi.bankFinishedUpdatingCustomFields(
        this.model.onboardingTemplate.bank_id,
        this.model.currentOrg.id,
        this.model.onboardingTemplate.template_id,
        this.model.initialCustomRequirementResults
      );
      this.snackbarStore.showSuccessSnackbarMessage('Additional info has been updated successfully.');
    } catch (e) {
      this.snackbarStore.showErrorSnackbarMessage(
        'An error has occurred saving additional info. Please contact support.'
      );
    } finally {
      runInAction(() => {
        this.model.isCompletingAdditionalInfo = false;
      });
    }
  });

  updateCurrentDispensary = action((dispensary: BankDispensary | MUO) => {
    this.model.currentOrg = dispensary;
    this.getUpdatedDispensaryTemplateResults(dispensary.id);
    if (dispensary.orgType === OrganizationType.DISPENSARY) {
      this.dispensaryStore.updateMinifiedDispensaryFromFullDispensary(dispensary);
    } else if (dispensary.orgType === OrganizationType.MUO) {
      this.dispensaryStore.updateMuo(dispensary);
    }
    this.sortDispensaries(this.dispensaryStore.dispensaries);
  });

  resendCurrentUserWelcomeEmail = action(async () => {
    try {
      this.model.resendWelecomeEmailLoading = true;
      if (!this.model.selectedUser) {
        throw new Error('No user selected');
      }
      await this.userApi.resendInviteEmail(this.model.selectedUser?.email);
      this.model.resendWelecomeEmailLoading = false;
      this.model.resendEmailSent = true;
    } catch (e) {
      this.snackbarStore.showErrorSnackbarMessage(
        'There was an issue sending the invite email. Contact support for additional help.'
      );
    }
  });

  setDispensaryStaff = action(async (orgId: string) => {
    this.model.dispensaryStaff = [];
    try {
      const dispensaryStaff = await this.dispensariesApi.getDispensaryStaff(orgId);
      this.setAccountUsers(dispensaryStaff);
      runInAction(() => {
        this.model.dispensaryStaff = dispensaryStaff;
      });
    } catch (error) {}
  });

  setUser = action((user: User) => {
    this.unsetUser();
    this.model.selectedUser = user;
    if (!this.isCurrentOrgArchived()) {
      this.getUserIdDocuments();
    }
    this.model.accountUserDrawerOpen = true;
    this.model.resendEmailSent = false;
  });

  getUser = action(async (id: string) => {
    const user = await this.userApi.getUser(id);
    return user;
  });

  unsetUser = action(() => {
    this.model.selectedUser = undefined;
    this.model.loadingIdDocuments = false;
    this.model.idFrontDocument = undefined;
    this.model.idBackDocument = undefined;
    this.model.idFrontInfo = undefined;
    this.model.idBackInfo = undefined;
    this.model.accountUserDrawerOpen = false;
  });

  inviteUser = action(async (data: any) => {
    try {
      this.model.invitingUser = true;
      const inviteUserRequest: InitialUserInfo = {
        email: data.email,
        firstName: data.firstName,
        lastName: data.lastName,
        role: data.role
      };
      if (data.pq_user_role !== undefined) {
        if (data.pq_user_role === 'none') {
          inviteUserRequest.pq_user_role = undefined;
        } else {
          inviteUserRequest.pq_user_role = data.pq_user_role;
        }
      }
      if (data.gcd_user_role !== undefined) {
        if (data.gcd_user_role === 'none') {
          inviteUserRequest.gcd_user_role = undefined;
        } else {
          inviteUserRequest.gcd_user_role = data.gcd_user_role;
        }
      }
      await this.userApi.inviteUser(this.bankStore.bank.id, this.model.currentOrg.id, inviteUserRequest);
      this.snackbarStore.showSuccessSnackbarMessage('User invited successfully.');
    } catch (error: any) {
      if (error.message === 'CRB does not allow profile editing or inviting other crb users') {
        this.snackbarStore.showErrorSnackbarMessage(
          'This dispensary does not allow profile editing or inviting new dispensary users. Contact support for additional help.'
        );
      } else if (error.message === "This user's email already exists in the organization.") {
        this.snackbarStore.showErrorSnackbarMessage(
          'A user with that email address already exists in this organization.'
        );
      } else {
        this.snackbarStore.showErrorSnackbarMessage(
          'There was an issue inviting the user. Contact support for additional help.'
        );
      }
    } finally {
      this.model.invitingUser = false;
      this.setAccountUsers(this.model.dispensaryStaff);
      this.toggleInviteUserModal();
    }
  });

  onEditUser = action(async (data: any, front: File[], back: File[]) => {
    try {
      if (this.model.selectedUser) {
        this.model.editUserLoading = true;
        if (front.length || back.length) {
          const docs = await this.updateIdentityDocs(front[0], back[0], this.model.selectedUser);
          this.model.idFrontInfo = docs?.frontDoc ? docs.frontDoc : this.model.idFrontInfo;
          this.model.idBackInfo = docs?.backDoc ? docs.backDoc : this.model.idBackInfo;
          if (docs?.updatedIdentification) {
            this.model.selectedUser.identification = docs?.updatedIdentification;
          }
        }
        const updatedUser = {
          ...data
        };
        if (data.dateOfBirth) {
          updatedUser.dateOfBirth = DateTimeHelpers.formatISOToDateString(
            data.dateOfBirth,
            this.bankStore.bank.iana_timezone
          );
        }
        if (this.model.selectedUser.identification) {
          updatedUser.identification = {
            idFront: {
              ...this.model.selectedUser.identification?.idFront,
              s3Key: this.model.idFrontInfo?.s3_key
            },
            idBack: {
              ...this.model.selectedUser.identification?.idBack,
              s3Key: this.model.idBackInfo?.s3_key
            },
            idType: data.idType,
            idNumber: data.idNumber,
            idState: data.idState,
            idIssueDate: data.idIssueDate,
            idExpirationDate: data.idExpirationDate
          };
        }

        delete updatedUser.idType;
        delete updatedUser.idNumber;
        delete updatedUser.idState;
        delete updatedUser.idIssueDate;
        delete updatedUser.idExpirationDate;
        delete updatedUser.role;

        const returnedUser = await this.userApi.updateUserDetails(
          this.bankStore.bank.id,
          this.model.currentOrg.id,
          this.model.selectedUser.id,
          updatedUser
        );
        this.model.editUserLoading = false;
        this.model.userEditModalOpen = false;
        this.model.loadingAccountUsers = true;

        if (returnedUser) {
          this.model.selectedUser = returnedUser;
          this.model.accountUsers = await this.getAccountUsers(this.model.currentOrg);
          const foundIdx = this.model.dispensaryStaff.findIndex((owner) => owner.id === returnedUser.id);
          if (foundIdx >= 0) {
            this.model.dispensaryStaff[foundIdx] = returnedUser;
          }
        }
        this.model.loadingAccountUsers = false;
        this.unsetUser();

        this.snackbarStore.showSuccessSnackbarMessage(`User has been updated successfully.`);
      }
    } catch {
      this.snackbarStore.showErrorSnackbarMessage(
        'There was an issue updating the user. Please contact support.'
      );
      this.model.loadingAccountUsers = false;
      this.model.userEditModalOpen = false;
      this.model.editUserLoading = false;
      this.unsetUser();
    }
  });

  updateIdentityDocs = action(
    async (newIdFront: File | undefined, newIdBack: File | undefined, user: User) => {
      try {
        const updatedIdentification = { ...user.identification };
        let frontDoc = undefined;
        let backDoc = undefined;
        if (newIdFront) {
          frontDoc = await this.uploadNewId(newIdFront, user);
          updatedIdentification.idFront = {
            filename: frontDoc.file_name,
            document_id: frontDoc.id,
            s3Key: frontDoc.s3_key,
            status: 'review'
          };
        }
        if (newIdBack) {
          backDoc = await this.uploadNewId(newIdBack, user);
          updatedIdentification.idBack = {
            filename: backDoc.file_name,
            document_id: backDoc.id,
            s3Key: backDoc.s3_key,
            status: 'review'
          };
        }

        return { frontDoc, backDoc, updatedIdentification };
      } catch (e) {
        this.snackbarStore.showSnackbar(SnackbarSeverity.Error, 'There was an issue uploading documents.');
      }
    }
  );

  uploadNewId = async (file: File, user: User) => {
    const s3Key = await uploadUserDocumentToS3(file, user.id);
    return await this.documentsApi.putUserDocument(file.name, s3Key, user.id);
  };

  toggleEditUserModal = action(() => {
    this.model.userEditModalOpen = !this.model.userEditModalOpen;
    if (this.model.userEditModalOpen) {
      this.model.accountUserDrawerOpen = false;
    }
  });

  toggleInviteUserModal = action(() => {
    this.model.inviteUserModalOpen = !this.model.inviteUserModalOpen;
  });

  resendWelcomeEmail = action(async (user: User) => {
    await this.userApi.resendInviteEmail(user.email);
  });

  loadPDFData = async (uri: string, id?: IdentificationDocument) => {
    // Chrome will not open a PDF from an about:blank URL, so we need the blob
    // to open the file in a new page. But, we don't need to wait here for it.
    fetch(uri).then(async (response) => {
      const blob = await response.blob();
      if (blob && id) {
        id.blob = blob;
      }
    });
  };

  getUserIdDocuments = action(async () => {
    if (this.model.selectedUser) {
      const docPromises = [];
      if (this.model.selectedUser?.identification?.idFront?.document_id) {
        if (!this.model.loadingIdDocuments) {
          this.model.loadingIdDocuments = true;
        }

        docPromises.push(
          await this.documentsApi
            .getUserDocument(
              this.model.selectedUser.id,
              this.model.selectedUser.identification.idFront.document_id
            )
            .then(async (doc) => {
              this.model.idFrontInfo = doc;
              await this.documentsApi
                .getDocumentLink(environment.storageConfig.userDocument, doc.s3_key, 'get')
                .then(async (s3Url) => {
                  await fetch(s3Url.s3LinkPath).then(async (response) => {
                    const base64 = await response.text();
                    const re = /(?:\.([^.]+))?$/;
                    const type = re.exec(doc.file_name)![1].toLowerCase();
                    this.model.idFrontDocument = {
                      base64,
                      type
                    };
                  });

                  if (doc.file_name.split('.').pop()?.toLowerCase() === 'pdf') {
                    if (this.model.idFrontDocument) {
                      this.loadPDFData(s3Url.s3LinkPath, this.model.idFrontDocument);
                    }
                  }
                });
            })
        );
      }
      if (this.model.selectedUser?.identification?.idBack?.document_id) {
        if (!this.model.loadingIdDocuments) {
          this.model.loadingIdDocuments = true;
        }

        docPromises.push(
          await this.documentsApi
            .getUserDocument(
              this.model.selectedUser.id,
              this.model.selectedUser.identification.idBack.document_id
            )
            .then(async (doc) => {
              await this.documentsApi
                .getDocumentLink(environment.storageConfig.userDocument, doc.s3_key, 'get')
                .then(async (s3Url) => {
                  this.model.idBackInfo = doc;
                  await fetch(s3Url.s3LinkPath).then(async (response) => {
                    const base64 = await response.text();
                    const re = /(?:\.([^.]+))?$/;
                    const type = re.exec(doc.file_name)![1].toLowerCase();
                    this.model.idBackDocument = {
                      base64,
                      type
                    };
                  });

                  if (doc.file_name.split('.').pop()?.toLowerCase() === 'pdf') {
                    if (this.model.idBackDocument) {
                      this.loadPDFData(s3Url.s3LinkPath, this.model.idBackDocument);
                    }
                  }
                });
            })
        );
      }
      await Promise.allSettled(docPromises).finally(() => {
        this.model.loadingIdDocuments = false;
      });
    }
  });

  removeIdDocument = action(
    async (idSide: 'front' | 'back', setLoading: React.Dispatch<React.SetStateAction<boolean>>) => {
      const deleteFrontIdDoc = action(async () => {
        if (this.model.selectedUser) {
          setLoading(true);
          const updatedIdentification = { ...this.model.selectedUser.identification };
          delete updatedIdentification.idFront;
          const user = await deleteIdDoc(updatedIdentification, this.model.selectedUser);
          if (user) {
            this.model.selectedUser = user;
          }
          this.model.idFrontDocument = undefined;
          this.model.idFrontInfo = undefined;
          setLoading(false);
        }
      });

      const deleteBackIdDoc = action(async () => {
        if (this.model.selectedUser) {
          setLoading(true);
          const updatedIdentification = { ...this.model.selectedUser.identification };
          delete updatedIdentification.idBack;
          const user = await deleteIdDoc(updatedIdentification, this.model.selectedUser);
          if (user) {
            this.model.selectedUser = user;
          }
          this.model.idBackDocument = undefined;
          this.model.idBackInfo = undefined;
          setLoading(false);
        }
      });

      const deleteIdDoc = action(
        async (
          updatedIdentification: {
            idType?: string | undefined;
            idNumber?: string | undefined;
            idState?: string | undefined;
            idIssueDate?: string | undefined;
            idExpirationDate?: string | undefined;
            idFront?: UserIdentification | undefined;
            idBack?: UserIdentification | undefined;
          },
          user: User
        ) => {
          try {
            const returnedUser = await this.userApi.updateUserIdentification(user.id, {
              identification: updatedIdentification
            });
            return returnedUser;
          } catch (e) {
            this.snackbarStore.showSnackbar(
              SnackbarSeverity.Error,
              'There was an issue removing the documents.'
            );
          }
        }
      );

      if (idSide === 'front') {
        deleteFrontIdDoc();
      } else {
        deleteBackIdDoc();
      }
    }
  );

  setSearchTerms = action((searchTerms: FilterListChild[]) => {
    this.model.searchTerms = searchTerms;
  });

  setAccountUsers = action(async (dispensaryStaff: User[]) => {
    const users = await this.getAccountUsers(this.model.currentOrg);
    const accountOwners = this.getAccountOwners(this.model.currentOrg, dispensaryStaff);
    if (this.model.currentOrg.orgType === OrganizationType.MUO) {
      const locations = this.dispensaryStore?.dispensaries?.filter(
        (disp) => disp.muo_id === this.model.currentOrg.id
      );
      if (locations.length) {
        let dispStaff: User[] = [];
        for (const location of locations) {
          const dispensaryStaff = await this.dispensariesApi.getDispensaryStaff(location.id);
          dispStaff = [...dispStaff, ...dispensaryStaff];
        }
        runInAction(() => {
          this.model.dispensaryStaff = [...this.model.dispensaryStaff, ...dispStaff];
        });
      }
    }
    runInAction(() => {
      this.model.accountUsers = users;
      this.model.accountOwners = accountOwners;
    });
  });

  getAccountUsers = async (dispensary: Dispensary | MUO) => {
    let users: MinifiedAccountUser[] = [];
    if (dispensary) {
      users = await this.userApi.getDispensaryUsers(this.bankStore.bank.id, dispensary.id);
    }
    return users;
  };

  getAccountOwners = (dispensary: Dispensary | MUO, dispensaryStaff: User[]) => {
    const users: User[] = [];
    if (dispensary && dispensary.groups && dispensary.groups.length > 0) {
      const accountOwnerGroup = dispensary.groups.find((item) => item.type === 'account_owner');

      if (accountOwnerGroup?.users && accountOwnerGroup?.users?.length > 0) {
        for (let index = 0; index < accountOwnerGroup?.users?.length; index++) {
          const accountOwner = accountOwnerGroup?.users[index];

          const staff = dispensaryStaff?.find((item) => item.id === accountOwner);
          if (staff && staff.invitation_status !== 'archived') {
            users.push(staff);
          }
        }
      }
    }
    return users;
  };

  openIdentificationFile = (id: IdentificationDocument) => {
    if (id.type === 'pdf' && id.blob) {
      openFileInNewWindow(id.blob, 'application/pdf');
    } else if (id.type === 'pdf') {
      // We're missing the blob! (But, that "will never happen.")
      this.snackbarStore.showErrorSnackbarMessage('There was an issue opening the image.');
    } else {
      this.openImage(id.base64, id.type);
    }
  };

  openImage = (base64string: string, type: string) => {
    const newTab = window.open();
    if (newTab) {
      if (type === 'jpg' || type === 'jpeg') {
        newTab.document.body.innerHTML = `<img src="data:image/jpeg;base64,${base64string}">`;
      } else if (type === 'png') {
        newTab.document.body.innerHTML = `<img src="data:image/png;base64,${base64string}">`;
      } else {
        this.snackbarStore.showErrorSnackbarMessage('There was an issue opening the image.');
      }
    } else {
      this.snackbarStore.showErrorSnackbarMessage('There was an issue opening the image.');
    }
  };

  inviteAccountOwner = async (userDetails: { [x: string]: any }): Promise<User | null> => {
    if (this.model.currentOrg.id) {
      const request: InviteNewUserRequest = {
        userInfo: {
          lastName: userDetails.lastName.trim(),
          firstName: userDetails.firstName.trim(),
          email: userDetails.email.trim(),
          role: 'dispensary_account_owner',
          pq_user_role: userDetails.pq_user_role === 'none' ? undefined : userDetails.pq_user_role,
          gcd_user_role: userDetails.gcd_user_role === 'none' ? undefined : userDetails.gcd_user_role
        },
        invitedByServiceProviderId: this.bankStore.bank.id
      };

      const result: { org: Dispensary; user: User } = await this.dispensariesApi.inviteDispensaryUser(
        this.model.currentOrg.id,
        request
      );
      const updatedBankDisp: BankDispensary = {
        ...(this.model.currentOrg as BankDispensary),
        groups: result.org.groups
      };
      this.updateCurrentDispensary(updatedBankDisp);
      this.setDispensaryStaff(result.org.id);
      return result.user;
    } else {
      return null;
    }
  };

  handleApprovePayqwickUserClick = async (userId: string) => {
    try {
      runInAction(() => {
        this.model.isApprovingPayqwickUserLoading = true;
        this.unsetUser();
      });
      const entityIds = this.model.currentOrgMetadata?.accounts
        ? Object.keys(this.model.currentOrgMetadata.accounts)
        : [];

      if (entityIds.length === 0) {
        this.snackbarStore.showErrorSnackbarMessage(
          `You must set at least one entity ID on the Internal tab`
        );
      } else {
        runInAction(() => {
          this.model.isApprovingPayqwickUserLoading = false;
          this.model.isPayqwickEntityModalOpen = true;
          this.model.currentPayqwickApprovalUserId = userId;
          this.model.currentPayqwickApprovalEntityIds = entityIds;
        });
      }
    } catch (e) {
      this.snackbarStore.showErrorSnackbarMessage(`There was an issue approving`);
      this.model.isPayqwickEntityModalOpen = false;
      this.model.isApprovingPayqwickUserLoading = false;
    }
  };

  closePayqwickApprovalModal = () => {
    this.model.isPayqwickEntityModalOpen = false;
    this.model.currentPayqwickApprovalUserId = undefined;
  };

  approvePayqwickUser = async (userId: string, eid: string, role: PayqwickUserRole) => {
    try {
      runInAction(() => {
        this.model.isApprovingPayqwickUserLoading = true;
      });
      const updatedUser = await this.banksApi.grantPayqwickAccess(
        this.bankStore.bank.id,
        this.model.currentOrg.id,
        userId,
        eid,
        role
      );
      runInAction(() => {
        const accountOwnerIndex = this.model.accountOwners.findIndex((u) => u.id === userId);
        if (accountOwnerIndex >= 0) {
          this.model.accountOwners.splice(accountOwnerIndex, 1, updatedUser);
        }

        const dispensaryStaffIndex = this.model.dispensaryStaff.findIndex((u) => u.id === userId);
        if (dispensaryStaffIndex >= 0) {
          this.model.dispensaryStaff.splice(dispensaryStaffIndex, 1, updatedUser);
        }
      });
      this.snackbarStore.showSuccessSnackbarMessage(`Successfully granted access to PayQwick`);
    } catch (e) {
      this.snackbarStore.showErrorSnackbarMessage(`There was an issue approving`);
    } finally {
      runInAction(() => {
        this.model.isApprovingPayqwickUserLoading = false;
        this.model.isPayqwickEntityModalOpen = false;
      });
    }
  };

  handleApproveGcdUserClick = (userId: string) => {
    try {
      runInAction(() => {
        this.model.isApprovingGcdUserLoading = true;
        this.unsetUser();
      });

      const user = this.model.dispensaryStaff.find((s) => s.id === userId);

      runInAction(() => {
        this.model.isApprovingGcdUserLoading = false;
        this.model.isGcdApprovalModalOpen = true;
        this.model.currentGcdApprovalUser = user;
      });
    } catch (e) {
      this.snackbarStore.showErrorSnackbarMessage(`There was an issue approving`);
      this.model.isGcdApprovalModalOpen = false;
      this.model.isApprovingGcdUserLoading = false;
    }
  };

  closeGcdApprovalModal = () => {
    this.model.isGcdApprovalModalOpen = false;
    this.model.currentGcdApprovalUser = undefined;
  };

  approveGcdUser = async (
    userId: string,
    role: GcdUserRole,
    account_accesses: {
      accountId: string;
      ownershipRole: GcdAccountOwnershipRole;
    }[]
  ) => {
    try {
      runInAction(() => {
        this.model.isApprovingGcdUserLoading = true;
      });
      const updatedUser = await this.banksApi.grantGcdAccess(
        this.bankStore.bank.id,
        this.model.currentOrg.id,
        userId,
        role,
        account_accesses
      );
      runInAction(() => {
        this.model.accountOwners.splice(
          this.model.accountOwners.findIndex((u) => u.id === userId),
          1,
          updatedUser
        );

        this.model.dispensaryStaff.splice(
          this.model.dispensaryStaff.findIndex((u) => u.id === userId),
          1,
          updatedUser
        );
      });
      this.snackbarStore.showSuccessSnackbarMessage(`Successfully granted access to GCD`);
    } catch (e) {
      this.snackbarStore.showErrorSnackbarMessage(`There was an issue approving`);
    } finally {
      runInAction(() => {
        this.model.isApprovingGcdUserLoading = false;
        this.model.isGcdApprovalModalOpen = false;
      });
    }
  };

  viewFile = (bucket: string, file: DocumentUpload) => {
    this.documentsApi.getDocumentLink(bucket, file.s3_key, 'get').then((s3Url) =>
      fetch(s3Url.s3LinkPath).then(async (response) => {
        const blob = await response.blob();
        openFileInNewWindow(blob, getMimeType(file.file_name), file.file_name);
      })
    );
  };

  getDocuments = action(async () => {
    this.model.loadingDocuments = true;

    const documents = await this.documentsApi.getDocuments(this.model.currentOrg.id);
    const documentsMap = getObjectMap(documents, 'id');
    const rootDocs = await this.dispensariesApi.getAllRootDocuments(this.model.currentOrg.id);

    runInAction(() => {
      this.model.allDocumentsMap = documentsMap;
      this.model.dispensaryAllRootDocuments = rootDocs;
      this.model.loadingDocuments = false;
    });
  });

  addNewDocuments = action((docs: DocumentUpload[]) => {
    for (const doc of docs) {
      this.model.allDocumentsMap[doc.id] = doc;
    }
  });

  rename = action(async (name: string) => {
    try {
      if (this.model.currentOrg.id) {
        this.model.renaming = true;
        const response = await this.banksApi.updateDispensaryNickname(
          this.bankStore.bank.id,
          this.model.currentOrg.id,
          name
        );
        this.updateCurrentDispensary(response);

        this.snackbarStore.showSuccessSnackbarMessage(`${name} has been updated successfully.`);
      }
    } catch (error) {
      this.snackbarStore.showErrorSnackbarMessage(`Could not update the name`);
    } finally {
      this.model.renaming = false;
    }
  });

  createParentForLocation = action(
    async (
      newName: string,
      sharedInternalId: boolean,
      accounts: AccountInternalMap,
      redirectCallback: (muoId: string) => void
    ) => {
      try {
        if (this.model.currentOrg.id && this.model.currentOrg.orgType === OrganizationType.DISPENSARY) {
          this.model.creatingLocation = true;

          const response = await this.banksApi.createLocation(
            this.bankStore.bank.id,
            this.model.currentOrg.id,
            { newName, sharedInternalId, accounts }
          );

          this.snackbarStore.showSuccessSnackbarMessage(`${newName} has been created successfully.`);
          redirectCallback(response.muo.id);
          return response;
        }
      } catch (error: any) {
        const friendlyMessage = lookupAccountNumberError(error);
        throw new Error(friendlyMessage);
      } finally {
        this.model.creatingLocation = false;
      }
    }
  );

  associateParent = action(async (dispensaryId: string, muoId: string, mergeSchedules: boolean) => {
    try {
      this.model.associatingParent = true;

      const response = await this.banksApi.associateParent(this.bankStore.bank.id, muoId, {
        dispensary_id: dispensaryId,
        merge_autogen_reviews: mergeSchedules
      });

      this.dispensaryStore.updateDispensaryMetadata(dispensaryId, response.dispensaryMetadata.value);
      this.dispensaryStore.updateDispensaryMetadata(muoId, response.muoMetdata.value);
      const muo = this.dispensaryStore.bankMuos.find((m) => m.id === muoId);
      const updatedDispensary: BankDispensary = { ...this.model.currentOrg, muo_id: muoId } as BankDispensary;
      this.updateCurrentDispensary(updatedDispensary);

      this.snackbarStore.showSuccessSnackbarMessage(
        `${updatedDispensary.name} has been associated with ${muo?.name ?? 'parent'} successfully.`
      );
    } catch (error) {
      this.snackbarStore.showErrorSnackbarMessage(`There was an issue associating with the parent account.`);
    } finally {
      this.model.associatingParent = false;
    }
  });

  private updateDispensaryWithNickName = (dispensary: Dispensary) => {
    const allDisps = [...this.dispensaryStore.dispensaries, ...this.dispensaryStore.dispensariesArchived];
    dispensary.name = allDisps.find((disp) => disp.id === dispensary.id)?.name ?? dispensary.name;
  };

  updatePeerGroup = action(async (dispId: string, peerGroupId: string) => {
    try {
      const response = await this.banksApi.addDispensaryToPeerGroup(
        this.bankStore.bank.id,
        peerGroupId,
        dispId
      );
    } catch {
      this.snackbarStore.showErrorSnackbarMessage(
        `There was an error associating the dispensary. Please contact support.`
      );
    }
  });

  setTemplateForDispensary = async (templateId: string, dispensaryId: string): Promise<void> => {
    // There is no reason here to return the CRBTemplates promise.
    return new Promise<void>((resolve, reject) => {
      this.templateApi
        .setTemplateForCrb(this.bankStore.bank.id, templateId, dispensaryId)
        .then(() => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  sortTemplatesByLabel = (templates: TemplateOption[]): TemplateOption[] => {
    // Alphabetical ascending.
    return templates.sort((a: TemplateOption, b: TemplateOption) => {
      if (a.label < b.label) {
        return -1;
      } else if (a.label === b.label) {
        return 0;
      } else {
        return 1;
      }
    });
  };

  getPublishedTemplates = () => {
    const fiTemplateStore = getFiTemplateStore();
    const templates = Object.keys(fiTemplateStore.activeTemplates.onboarding)
      .map((key) => {
        const { template_id, name, published_date, plant_touching } = {
          ...fiTemplateStore.templates.onboarding[key]
        };
        return {
          value: template_id,
          label: name,
          published: published_date !== undefined,
          plant_touching: plant_touching
        } as TemplateOption;
      })
      .filter((t) => t.published);

    return sortArrayByField(templates, 'label');
  };

  addDocumentToOnboardingDocumentRequirement = action(
    async (requirementId: string, documents: CreateDocumentsRequest) => {
      this.templateApi
        .addOnboardingDocumentRequirementDocumentsFiV2(
          this.bankStore.bank.id,
          this.model.currentOrg.id,
          this.model.onboardingTemplateResult.template_id,
          requirementId,
          documents
        )
        .then((requirementResult) => {
          runInAction(() => {
            this.getDocuments();
            this.updateOnboardingDocumentRequirementResult(requirementResult);
          });
        });
    }
  );

  setForceReloadQuestionnaires = action(async (reload: boolean) => {
    this.model.forceReloadQuestionnaires = reload;
  });

  renameFriendlyDocumentName = action(
    async (
      data: Record<string, unknown>,
      crbId: string,
      bankId: string,
      requirementId: string,
      templateId: string
    ) => {
      try {
        await this.banksApi
          .renameDocumentFriendlyName(bankId, crbId, templateId, requirementId, { filenames: data })
          .then((requirementResult) => {
            runInAction(() => {
              this.updateOnboardingDocumentRequirementResult(requirementResult);
              this.getDocuments();
            });
          });
      } catch (error) {
        this.snackbarStore.showErrorSnackbarMessage(`Could not update the name`);
      }
    }
  );
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setAccountsFilterRef = action(async (accountsFilterRef: React.MutableRefObject<any>) => {
    this.model.accountsFilterRef = accountsFilterRef;
  });

  onClose = () => {
    runInAction(() => {
      this.model.docId = '';
      this.model.openDrawer = false;
    });
  };

  viewFileCommentsBar = (docId: string) => {
    runInAction(() => {
      this.model.docId = docId;
      this.model.openDrawer = true;
    });
  };

  // Operational details tab

  updateDispensaryOperationalDetails = action(async (data: FieldValues) => {
    this.model.savingOperationalDetails = true;

    Object.entries(data).forEach(([key, value]) => {
      if (value === '--') {
        delete data[key];
      }
    });

    try {
      const disp = await this.banksApi.updateOperationalDetails(
        this.bankStore.bank.id,
        this.model.currentOrg.id,
        data
      );

      this.updateCurrentDispensary(disp);
      this.snackbarStore.showSnackbar(
        SnackbarSeverity.Success,
        'Operational details have been updated successfully.'
      );
    } catch (e) {
      this.snackbarStore.showSnackbar(
        SnackbarSeverity.Error,
        'There was an issue updating operational details.'
      );

      throw e;
    } finally {
      runInAction(() => {
        this.model.savingOperationalDetails = false;
      });
    }
  });

  setAwaitingReviewCount = action((count: number) => {
    this.model.awaitingReviewCount = count;
  });
}

export const actions = new Actions(model);
